import type { Admission } from "@/core/admissions/types";
import { getHospitalRevenueSettings } from "@/core/admin/establishment-store";
import type { SurgeryCase } from "@/core/surgery/types";
import {
  createAndIssueActBillingDocument,
  getBillingDocumentByAdmissionAndSource,
  getHospitalizationBillForStayOnDay,
} from "@/core/billing/billing-documents/billing-documents-store";
import { listActiveStays } from "@/core/hospitalization/hospitalization-store";
import type { HospitalStay } from "@/core/hospitalization/types";
import type { VitalSignRecord } from "@/core/nursing/types";
import { startOfDay } from "@/core/analytics/analytics-utils";

function formatNightLabel(day: Date): string {
  return day.toLocaleDateString("fr-FR");
}

function hospitalizationBillingStart(stay: HospitalStay): Date {
  const raw = stay.bedAssignedAt ?? stay.admittedAt;
  return startOfDay(new Date(raw));
}

/** Une nuitée hospitalisation pour un séjour actif et un jour donné. */
export async function ensureHospitalizationDailyBill(
  stay: HospitalStay,
  day: Date,
): Promise<void> {
  if (stay.status !== "active" || !stay.bedId) return;

  const fee = getHospitalRevenueSettings().hospitalizationDailyFee;
  if (fee <= 0) return;

  const billingDay = startOfDay(day);
  if (getHospitalizationBillForStayOnDay(stay.id, billingDay)) return;

  const nightLabel = formatNightLabel(billingDay);
  await createAndIssueActBillingDocument({
    patientId: stay.patientId,
    source: "hospitalization",
    serviceLabel: `${stay.wardName || "Hospitalisation"} — nuitée ${nightLabel}`,
    unitPrice: fee,
    linePrefix: "Hospitalisation",
    admissionId: stay.admissionId,
    stayId: stay.id,
    forcePatientPayer: true,
    allowMultiplePerStay: true,
  });
}

/** Rattrape les nuitées manquantes du début de séjour jusqu'à aujourd'hui. */
export async function syncRecurringHospitalizationBillsForStay(
  stay: HospitalStay,
): Promise<void> {
  if (stay.status !== "active" || !stay.bedId) return;

  const start = hospitalizationBillingStart(stay);
  const end = startOfDay(new Date());
  const cursor = new Date(start);

  while (cursor <= end) {
    await ensureHospitalizationDailyBill(stay, cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
}

/** Tous les séjours actifs avec lit — appelé au démarrage et sur la page hospitalisation. */
export async function syncRecurringHospitalizationBills(): Promise<void> {
  const activeStays = listActiveStays().filter((s) => s.status === "active" && s.bedId);
  for (const stay of activeStays) {
    await syncRecurringHospitalizationBillsForStay(stay);
  }
}

/** @deprecated Utiliser syncRecurringHospitalizationBillsForStay — conservé pour compat. */
export async function autoBillHospitalizationOnBedAssign(stay: HospitalStay): Promise<void> {
  await syncRecurringHospitalizationBillsForStay(stay);
}

/** Forfait passage urgences à l'orientation. */
export async function autoBillEmergencyOnOrient(admission: Admission): Promise<void> {
  if (admission.channel !== "emergency" || admission.status !== "oriented") return;
  if (getBillingDocumentByAdmissionAndSource(admission.id, "emergency")) return;

  const fee = getHospitalRevenueSettings().emergencyVisitFee;
  if (fee <= 0) return;

  const dest = admission.destinations[0] ?? admission.destination;
  await createAndIssueActBillingDocument({
    patientId: admission.patientId,
    source: "emergency",
    serviceLabel: dest?.trim() || "Urgences",
    unitPrice: fee,
    linePrefix: "Urgences",
    admissionId: admission.id,
    forcePatientPayer: true,
  });
}

/** Acte de soins infirmiers — une facture par admission et par jour. */
export async function autoBillNursingOnVitals(record: VitalSignRecord): Promise<void> {
  if (!record.admissionId) return;

  const fee = getHospitalRevenueSettings().nursingCareFee;
  if (fee <= 0) return;

  const today = startOfDay(new Date()).toDateString();
  const existing = getBillingDocumentByAdmissionAndSource(record.admissionId, "nursing");
  if (existing) {
    const issued = new Date(existing.issuedAt ?? existing.createdAt).toDateString();
    if (issued === today) return;
  }

  await createAndIssueActBillingDocument({
    patientId: record.patientId,
    source: "nursing",
    serviceLabel: record.context === "emergency" ? "Soins urgences" : "Soins infirmiers",
    unitPrice: fee,
    linePrefix: "Soins infirmiers",
    admissionId: record.admissionId,
    stayId: record.stayId,
    forcePatientPayer: true,
  });
}

/** Orientation accueil — urgences et destinations chirurgicales. */
export async function autoBillAdmissionOnOrient(admission: Admission): Promise<void> {
  if (admission.status !== "oriented") return;
  await autoBillEmergencyOnOrient(admission);

  const dests = admission.destinations.length
    ? admission.destinations
    : admission.destination
      ? [admission.destination]
      : [];

  for (const dest of dests) {
    if (!/chirurg/i.test(dest)) continue;
    if (getBillingDocumentByAdmissionAndSource(admission.id, "surgery")) break;

    const fee = getHospitalRevenueSettings().surgeryProcedureFee;
    if (fee <= 0) break;

    await createAndIssueActBillingDocument({
      patientId: admission.patientId,
      source: "surgery",
      serviceLabel: dest,
      unitPrice: fee,
      linePrefix: "Orientation chirurgie",
      admissionId: admission.id,
      forcePatientPayer: true,
    });
    break;
  }

  if (admission.channel === "emergency" && admission.arrivalMode === "ambulance") {
    if (!getBillingDocumentByAdmissionAndSource(admission.id, "ambulance")) {
      const fee = getHospitalRevenueSettings().ambulanceTransportFee;
      if (fee > 0) {
        await createAndIssueActBillingDocument({
          patientId: admission.patientId,
          source: "ambulance",
          serviceLabel: "Transport ambulance",
          unitPrice: fee,
          linePrefix: "Ambulance",
          admissionId: admission.id,
          forcePatientPayer: true,
        });
      }
    }
  }
}

/** Acte opératoire détaillé — facturation à la clôture du dossier bloc. */
export async function autoBillSurgeryCaseOnComplete(surgeryCase: SurgeryCase): Promise<void> {
  if (surgeryCase.status !== "completed") return;

  const fee = surgeryCase.procedureFee > 0
    ? surgeryCase.procedureFee
    : getHospitalRevenueSettings().surgeryProcedureFee;
  if (fee <= 0) return;

  const durationLabel =
    surgeryCase.durationMinutes > 0 ? ` (${surgeryCase.durationMinutes} min)` : "";

  await createAndIssueActBillingDocument({
    patientId: surgeryCase.patientId,
    source: "surgery",
    serviceLabel: `${surgeryCase.procedureName} — ${surgeryCase.surgeonName}${durationLabel}`,
    unitPrice: fee,
    linePrefix: "Acte chirurgical",
    admissionId: surgeryCase.admissionId,
    surgeryCaseId: surgeryCase.id,
    forcePatientPayer: true,
  });
}

export function registerExtendedHospitalBilling(): void {
  void syncRecurringHospitalizationBills();
}

registerExtendedHospitalBilling();
